import { DOCUMENT } from '@angular/common';
import { ElementRef, inject, Injectable } from '@angular/core';
import {
  CompanyTriple,
  ExhibitorDetail,
  ExhibitorDetails,
  ExhibitorSponsorBanner,
  ExhibitorWelcomeMessageModalViewModel,
  WelcomeMessage,
  WelcomeMessageModalExhibitorDetail
} from '@core/data-access/models/exhibitor/exhibitor.model';
import { ScrollToService } from '@core/data-access/services/scroll-to.service';
import { selectIsHighContrastEnabled } from '@core/data-access/store/accessibility/accessibility.selectors';
import { ExhibitorActions } from '@core/data-access/store/exhibitor/exhibitor.actions';
import {
  selectActiveExhibitorCompanyTriple,
  selectExhibitorDetailByCompanyTriple,
  selectExhibitorSponsorBannerByCompanyTriple,
  selectIsExhibitorWelcomeMessageModalOpen
} from '@core/data-access/store/exhibitor/exhibitor.selectors';
import { injectStyleElement } from '@core/utils/dom-helpers';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { find, isEmpty, isNil, keys, pickBy, pull, random, some, unset } from 'lodash';
import { combineLatest, filter, map, Observable, of, pipe, startWith, switchMap, tap, withLatestFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ExhibitorService {
  readonly #store = inject(Store);
  readonly #document = inject(DOCUMENT);
  readonly #translateService = inject(TranslateService);
  readonly #scrollToService = inject(ScrollToService);

  setExhibitorSponsorBanner$(activeExhibitorDetailCompanyTriple: CompanyTriple, sponsorBannerElementRef: ElementRef) {
    return this.#store.select(selectExhibitorSponsorBannerByCompanyTriple(activeExhibitorDetailCompanyTriple)).pipe(
      this.isEmptySponsorBannerFilterPipe(),
      tap(() =>
        this.#scrollToService.setSponsorBannerElement(sponsorBannerElementRef?.nativeElement).scrollToSponsorBanner()
      )
    );
  }

  setRandomExhibitorSponsorBanner$(
    exhibitorDetails: ExhibitorDetails,
    activeExhibitorDetailCompanyTriple: CompanyTriple
  ) {
    return of({ exhibitorDetails, activeExhibitorDetailCompanyTriple }).pipe(
      map(this.randomizeCompanyTriple),
      filter((randomizedCompanyTriple) => !isNil(randomizedCompanyTriple)),
      tap((randomizedCompanyTriple) =>
        this.#store.dispatch(ExhibitorActions.setActiveExhibitorCompanyTriple(randomizedCompanyTriple))
      ),
      switchMap((randomizedCompanyTriple) =>
        this.#store.select(selectExhibitorSponsorBannerByCompanyTriple(randomizedCompanyTriple))
      ),
      this.isEmptySponsorBannerFilterPipe()
    );
  }

  getExhibitorWelcomeMessageModalViewModel$(): Observable<ExhibitorWelcomeMessageModalViewModel> {
    const currentLanguageCode$ = this.#translateService.onLangChange.pipe(
      map(({ lang }) => lang),
      startWith(this.#translateService.currentLang)
    );

    return combineLatest({
      currentLanguageCode: currentLanguageCode$,
      activeExhibitorDetailCompanyTriple: this.#store.select(selectActiveExhibitorCompanyTriple),
      isExhibitorWelcomeMessageModalOpen: this.#store.select(selectIsExhibitorWelcomeMessageModalOpen)
    }).pipe(
      filter(({ activeExhibitorDetailCompanyTriple }) => !isNil(activeExhibitorDetailCompanyTriple)),
      switchMap(({ currentLanguageCode, activeExhibitorDetailCompanyTriple, isExhibitorWelcomeMessageModalOpen }) =>
        this.setExhibitorWelcomeMessageModalViewModel$(
          currentLanguageCode,
          activeExhibitorDetailCompanyTriple,
          isExhibitorWelcomeMessageModalOpen
        )
      ),
      withLatestFrom(this.#store.select(selectIsHighContrastEnabled)),
      map(([exhibitorWelcomeMessageModalViewModel, isHighContrastEnabled]) => ({
        exhibitorWelcomeMessageModalViewModel,
        isHighContrastEnabled
      })),
      tap(
        ({
          exhibitorWelcomeMessageModalViewModel: {
            exhibitorDetail: { welcomeMessageBackgroundColor }
          },
          isHighContrastEnabled
        }) => this.changeWelcomeMessageBackgroundColor(welcomeMessageBackgroundColor, isHighContrastEnabled)
      ),
      map(({ exhibitorWelcomeMessageModalViewModel }) => exhibitorWelcomeMessageModalViewModel)
    );
  }

  isEmptyExhibitorWelcomeMessageModal(exhibitorDetail: ExhibitorDetail) {
    const { logo, hallId, floorId, standId, welcomeMessages } = exhibitorDetail;

    const currentLanguageCode = this.#translateService.currentLang;
    const currentLanguageCodeOrDefaultWelcomeMessageTranslationExists = some(
      welcomeMessages,
      ({ languageCode }) => languageCode === currentLanguageCode || languageCode === null
    );

    return (
      isNil(logo) &&
      isNil(hallId) &&
      isNil(floorId) &&
      isNil(standId) &&
      !currentLanguageCodeOrDefaultWelcomeMessageTranslationExists
    );
  }

  private isEmptySponsorBannerFilterPipe() {
    return pipe<Observable<ExhibitorSponsorBanner>, Observable<ExhibitorSponsorBanner>>(
      filter((exhibitorSponsorBanner) => !isNil(exhibitorSponsorBanner) && !isNil(exhibitorSponsorBanner.sponsorBanner))
    );
  }

  private randomizeCompanyTriple({
    exhibitorDetails,
    activeExhibitorDetailCompanyTriple
  }: {
    exhibitorDetails: ExhibitorDetails;
    activeExhibitorDetailCompanyTriple: CompanyTriple;
  }) {
    const exhibitionDetailsWithBanner = pickBy(exhibitorDetails, (exhibitionDetail) => exhibitionDetail.banner);
    const inactiveCompanyTriples = pull(keys(exhibitionDetailsWithBanner), activeExhibitorDetailCompanyTriple);

    if (isEmpty(inactiveCompanyTriples)) {
      return null;
    }

    const inactiveCompanyTripleIndex = random(0, inactiveCompanyTriples.length - 1);

    return inactiveCompanyTriples[inactiveCompanyTripleIndex];
  }

  private setExhibitorWelcomeMessageModalViewModel$(
    currentLanguageCode: string,
    activeExhibitorDetailCompanyTriple: CompanyTriple,
    isExhibitorWelcomeMessageModalOpen: boolean
  ) {
    return this.#store.select(selectExhibitorDetailByCompanyTriple(activeExhibitorDetailCompanyTriple)).pipe(
      filter((activeExhibitorDetail) => !isNil(activeExhibitorDetail)),
      map((activeExhibitorDetail) => {
        const exhibitorWelcomeMessageModalViewModel: ExhibitorWelcomeMessageModalViewModel = {
          exhibitorDetail: { ...activeExhibitorDetail, welcomeMessage: null },
          isEmptyExhibitorWelcomeMessageModal: false,
          isExhibitorWelcomeMessageModalOpen
        };

        unset(exhibitorWelcomeMessageModalViewModel, 'exhibitorDetail.welcomeMessages');

        const currentLanguageCodeWelcomeMessage = this.findLanguageCode(
          activeExhibitorDetail.welcomeMessages,
          currentLanguageCode
        );

        if (currentLanguageCodeWelcomeMessage) {
          exhibitorWelcomeMessageModalViewModel.exhibitorDetail.welcomeMessage = currentLanguageCodeWelcomeMessage;

          return exhibitorWelcomeMessageModalViewModel;
        }

        const defaultWelcomeMessage = this.findLanguageCode(activeExhibitorDetail.welcomeMessages, null);

        if (defaultWelcomeMessage) {
          exhibitorWelcomeMessageModalViewModel.exhibitorDetail.welcomeMessage = defaultWelcomeMessage;
        } else {
          exhibitorWelcomeMessageModalViewModel.exhibitorDetail.welcomeMessage = null;
        }

        exhibitorWelcomeMessageModalViewModel.isEmptyExhibitorWelcomeMessageModal =
          this.setIsEmptyExhibitorWelcomeMessageModalViewModel(exhibitorWelcomeMessageModalViewModel.exhibitorDetail);

        return exhibitorWelcomeMessageModalViewModel;
      })
    );
  }

  private findLanguageCode(welcomeMessages: WelcomeMessage[], findLanguageCode: string) {
    return find(welcomeMessages, ({ languageCode }) => languageCode === findLanguageCode);
  }

  private setIsEmptyExhibitorWelcomeMessageModalViewModel(
    welcomeMessageModalExhibitorDetail: WelcomeMessageModalExhibitorDetail
  ) {
    const { logo, hallId, floorId, standId, welcomeMessage } = welcomeMessageModalExhibitorDetail;

    return isNil(logo) && isNil(hallId) && isNil(floorId) && isNil(standId) && isNil(welcomeMessage);
  }

  private changeWelcomeMessageBackgroundColor(welcomeMessageBackgroundColor: string, isHighContrastEnabled: boolean) {
    const welcomeMessageModalSelectors = [
      '#exhibitor-welcome-message-modal .p-dialog',
      '#exhibitor-welcome-message-modal .p-dialog-header',
      '#exhibitor-welcome-message-modal .p-dialog-content'
    ];

    const changeWelcomeMessageBackgroundColorCss = !isHighContrastEnabled
      ? welcomeMessageModalSelectors.reduce(
          (previousCss, currentCss) =>
            `${previousCss} ${currentCss} { background-color: ${welcomeMessageBackgroundColor} !important; }`,
          ''
        )
      : '';

    injectStyleElement(this.#document, changeWelcomeMessageBackgroundColorCss, 'exhibitor-welcome-message-modal');
  }
}
